import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCustomerComplaints } from 'redux/actions/CustomerComplaints';
import Loader from 'components/loader/Loader';
import CommonTable from 'components/commonTable/CommonTable';
import { dateFormate } from 'utils/utils';
import { debounce } from 'lodash';
import CustomerBreadCrumb from 'pages/customers/CustomerBreadCrumb';
import LocationDropdown from 'components/LocationDropdown/LocationDropdown';
import { filterCustomerComplaintData } from './FilterCustomerComplaintData';
import { Checkbox } from 'components/ui/checkbox';


function CustomerComplaintsTable() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = {};
  searchParams.forEach((value, key) => {
    params[key] = value;
  });

  const [page, setPage] = useState(params?.page ? parseInt(params?.page) : 0);
  const [rowsPerPage, setRowsPerPage] = useState(params?.pageSize ? parseInt(params?.pageSize) : 10);
  const [status, setStatus] = useState(params?.status ? params?.status : []);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState(params?.search ? params?.search : '');
  const tableData = useSelector((state) => state?.userCustomerComplaints?.userCustomerComplaints?.data);
  const loader = useSelector((state) => state?.userCustomerComplaints?.loading);
  const selectedLocation = useSelector((state) => state?.locationDropdown?.selectedLocation);
  const [sosOnly, setSosOnly] = useState(false);

  let filterData = {
    page: params?.search ? 0 : page,
    dateFrom: dateFrom,
    dateTo: dateTo,
    status: status,
    pageSize: rowsPerPage,
    search: search,
  };

  useEffect(() => {
    dispatch(getCustomerComplaints(filterData));
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setSearchParams({ ...params, page: newPage });
    const newFilterData = {
      ...filterData,
      page: newPage,
      complainType: sosOnly ? 'sos' : ''
    };
    dispatch(getCustomerComplaints(newFilterData));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setSearchParams({
      ...params,
      page: 0,
      pageSize: newRowsPerPage,
    });
    const newFilterData = {
      ...filterData,
      page: 0,
      pageSize: newRowsPerPage,
      complainType: sosOnly ? 'sos' : ''
    };
    dispatch(getCustomerComplaints(newFilterData));
  };

  const onFilterChange = (val) => {
    setPage(0);
    setStatus(val);
    filterData = { ...filterData, status: val.toString(), page: 0 };
    setSearchParams({
      ...params,
      status: val.toString(),
      page: 0,
    });
    dispatch(getCustomerComplaints(filterData));
  };

  const onRestFilter = () => {
    setStatus('');
    setSearchParams({ ...params, status: '' });
    filterData = { ...filterData, status: '' };
    dispatch(getCustomerComplaints(filterData));
  };

  const onSelectDateRange = (newValue) => {
    setDateFrom(dateFormate(newValue[0]).format('MM/DD/YYYY'));
    setDateTo(dateFormate(newValue[1]).format('MM/DD/YYYY'));
    filterData = {
      ...filterData,
      dateFrom: dateFormate(newValue[0]).format('MM/DD/YYYY'),
      dateTo: dateFormate(newValue[1]).format('MM/DD/YYYY'),
    };
    dispatch(getCustomerComplaints(filterData));
  };

  const onSearch = debounce((e) => {
    setSearch(e);
    setPage(0);
    filterData = { ...filterData, search: e, page: 0 };
    setSearchParams({ ...params, search: e, page: 0, pageSize: '' });
    dispatch(getCustomerComplaints(filterData));
  }, 1000);

  const onClickComplaint = (navigate, val) => {
    navigate(`/complaints/detail`, { state: { id: val?.id, name: val?.complainId, demandProfileId: val?.raisedBy}});
  };

  const handleLocationChange = (selectedLocation) => {
    dispatch(getCustomerComplaints({ ...filterData, location: selectedLocation?.id }));
  }

  return (
    <>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: { lg: 'space-between', md: 'space-between', sm: 'flex-start', xs: 'flex-start' },
        alignItems: { lg: 'center', md: 'center', sm: 'flex-start', xs: 'flex-start' },
        flexDirection: { lg: 'row', md: 'row', sm: 'column', xs: 'column' },
        gap: { lg: 4, md: 4, sm: 2, xs: 2 }
      }}>
        <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', sm: 'column', md: 'row', lg: 'row' }, width: { xs: '100%', sm: '100%', md: 'auto', lg: 'auto' } }}>
          <Box sx={{ width: { xs: '100%', sm: '100%', md: '200px', lg: '200px' } }}>
            <LocationDropdown
              onChange={handleLocationChange}
              placeholder="All City"
              fullWidth
            />
          </Box>
          <label className="flex items-center gap-2 text-[#000] text-[12px] font-semibold leading-none">
            <Checkbox
              checked={sosOnly}
              onCheckedChange={(checked) => {
                const isChecked = checked === true;
                setSosOnly(isChecked);
                setPage(0); // Reset to first page for new filter
                const newFilterData = {
                  page: 0,
                  dateFrom,
                  dateTo,
                  status,
                  pageSize: rowsPerPage,
                  search,
                  location: selectedLocation?.id,
                  complainType: isChecked ? 'sos' : ''
                };
                dispatch(getCustomerComplaints(newFilterData));
              }}
            />
            SOS only
          </label>
        </Box>
        <Box sx={{ width: { xs: '100%', sm: '100%', md: 'auto', lg: 'auto' } }}>
          <CustomerBreadCrumb
            defaultStatus={status}
            filterdata={filterCustomerComplaintData}
            onFilterChange={onFilterChange}
            onRestFilter={onRestFilter}
            onSelectDateRange={onSelectDateRange}
            onSearch={onSearch}
            searchValue={search}
            tabName='complaints'
            filter={true}
          />
        </Box>
      </Box>
      {loader ? (
        <Loader />
      ) : (
        <CommonTable
          data={tableData}
          tableOnClickRow={onClickComplaint}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          tabName={'complaints'}
          isClickable={true}
        />
      )}
    </>
  );
}

export default CustomerComplaintsTable;
